import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { ApiService } from '../api.service'
import { Subscription } from 'rxjs'
import mermaid from 'mermaid'
import * as pako from 'pako'

@Component({
	selector: 'diagram',
	templateUrl: './diagram.component.html',
	styleUrls: ['./diagram.component.scss']
})

export class DiagramComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {

	@Input() courseID = 0
	@Input() parentType = 'heading'
	@Input() parentID = 0
	@Input() dialog = true
	edit = false
	
	id = 0
	title = ''
	code = ''
	width = 0
	height = 0
	align = 'down'
	fileName = ''
	liveUrl = ''
	aiUrl = ''

	diagramLoading = false
	updateDiagramLoading = false
	updateDiagramSuccess = false
	updateDiagramError = false
	generateDiagramLoading = false
	generateDiagramSuccess = false
	generateDiagramError = false

	@ViewChild('diagramShow', { static: false }) diagramShowDiv!: ElementRef;

	getDiagramSub = new Subscription
	updateDiagramSub = new Subscription
	deleteDiagramSub = new Subscription
	downloadDiagramImageSub = new Subscription
	downloadDiagramImageInkSub = new Subscription
	generateDiagramSub = new Subscription
	
	constructor(private dataService: ApiService) {}

	ngOnInit(): void {}
	
	ngAfterViewInit(): void {
		mermaid.initialize({
			startOnLoad: true,
		})
	}

	ngOnChanges(): void {
		this.getDiagram()
		this.toggleEdit(false)
	}

	ngOnDestroy(): void {
		this.getDiagramSub?.unsubscribe()
		this.updateDiagramSub?.unsubscribe()
		this.deleteDiagramSub?.unsubscribe()
		this.downloadDiagramImageSub?.unsubscribe()
		this.downloadDiagramImageInkSub?.unsubscribe()
		this.generateDiagramSub?.unsubscribe()
	}

	getDiagram() {
		this.diagramLoading = true

		this.getDiagramSub = this.dataService.getDiagram(this.parentType, this.parentID)
		.subscribe({
			next: (data) => {
				console.log(data)

				this.diagramLoading = false
				if (data != null) {
					this.id = +data.id
					if (this.courseID == 0) this.courseID = +data.course
					this.title = data.title
					this.code = data.code
					this.width = +data.width
					this.height = +data.height
					this.align = data.align
					this.fileName = `${this.courseID}/diagram_${this.parentType}_${this.parentID}`
					
					this.updateDiagramURL()
					this.renderDiagram()
				}
			},
			error: (e) => {
				console.log(e)
			},
		})
	}

	toggleEdit(edit: boolean) {
		this.updateDiagramLoading = false
		this.updateDiagramSuccess = false
		this.updateDiagramError = false
		this.generateDiagramLoading = false
		this.generateDiagramSuccess = false
		this.generateDiagramError = false
		this.edit = edit
		if (!edit) this.renderDiagram()
	}

	updateDiagramURL() {
		let code = this.code
		if (code == '') return

		const data = {
			code,
			mermaid: { 
				theme: 'default',
				fontFamily: 'Liberation Sans',
			},
			updateEditor: true,
		}

		const json = JSON.stringify(data)
		const compressed = pako.deflate(json)
		const base64 = btoa(String.fromCharCode(...new Uint8Array(compressed)))
			.replace(/\+/g, '-')
			.replace(/\//g, '_')
			.replace(/=+$/, '')

		this.liveUrl = `https://mermaid.live/edit#pako:${base64}`
		this.aiUrl = `https://mermaid.ai/play#pako:${base64}`
	}

	async renderDiagram() {
		if (this.code == '') return
		if (!this.diagramShowDiv) return
		const container = this.diagramShowDiv.nativeElement
		container.innerHTML = '<i class="fas fa-spinner fa-spin"></i><span>Cargando...</span>'

		try {
			const { svg } = await mermaid.render('heading-diagram', this.code)
			container.innerHTML = svg
			let svgElement = container.querySelector('svg') as SVGSVGElement
			this.width = Math.round(svgElement.viewBox.baseVal.width)
			this.height = Math.round(svgElement.viewBox.baseVal.height)
			// this.downloadDiagramImage(svgElement)
			this.downloadDiagramImageInk()
			this.updateDiagram()
		} catch (err) {
			console.error('Error al renderizar Mermaid:', err)
			container.innerHTML = `<div class="text-danger fw-bold"><p>Error al renderizar el diagrama:</p><p>${err}</p></div>`
		}
	}

	viewDiagram() {
		let url = `${this.dataService.remoteBaseUrl}/media/img/${this.fileName}.png`
		window.open(url)
	}

	updateDiagram(event?: Event) {
		if (event != null) {
			this.updateDiagramLoading = true
			this.updateDiagramSuccess = false
			this.updateDiagramError = false
		}
		
		this.updateDiagramSub = this.dataService.updateDiagram(this.id, this.courseID, this.parentType, this.parentID, this.title, this.code, this.width, this.height, this.align)
		.subscribe({
			next: (data) => {
				console.log(data)
				if (event != null) {
					this.updateDiagramLoading = false
					this.updateDiagramSuccess = true
					this.updateDiagramError = false
				}
				if (this.id == 0) this.id = +data
			},
			error: (e) => {
				console.log(e)
				if (event != null) {
					this.updateDiagramLoading = false
					this.updateDiagramSuccess = false
					this.updateDiagramError = true
				}
			},
		})
	}

	deleteDiagram() {
		this.diagramLoading = true
		this.updateDiagramLoading = false
		this.updateDiagramSuccess = false
		this.updateDiagramError = false
		this.generateDiagramLoading = false
		this.generateDiagramSuccess = false
		this.generateDiagramError = false

		this.deleteDiagramSub = this.dataService.deleteDiagram(this.id)
		.subscribe({
			next: (data) => {
				console.log(data)
				this.diagramLoading = false
				this.id = 0
				this.title = ''
				this.code = ''
				this.liveUrl = ''
				this.aiUrl = ''
				this.width = 0
				this.height = 0
				this.align = 'down'
				this.fileName = ''
			},
			error: (e) => {
				console.log(e)
			},
		})
	}

	async downloadDiagramImage(svg: SVGSVGElement) {
		const svgData = new XMLSerializer().serializeToString(svg)
		const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' })

		const reader = new FileReader()
		reader.onload = () => {
			const img = new Image()
			img.onload = () => {
				// Crear canvas con dimensiones
				const canvas = document.createElement('canvas')
				canvas.width = this.width
				canvas.height = this.height

				// Dibujar fondo transparante y diagrama
				const ctx = canvas.getContext('2d')!
				ctx.fillStyle = 'transparent'
				ctx.fillRect(0, 0, this.width, this.height)
				ctx.drawImage(img, 0, 0, this.width, this.height)

				// Convierte el canvas a PNG
				const pngData = canvas.toDataURL('image/png')

				this.downloadDiagramImageSub = this.dataService.downloadDiagramImage(this.fileName, pngData)
				.subscribe({
					next: (data) => {
						console.log(data)
					},
					error: (e) => {
						console.log(e)
					},
				})
			}
			img.src = reader.result as string
		}
		reader.readAsDataURL(svgBlob)
	}

	async downloadDiagramImageInk()
	{
		this.downloadDiagramImageInkSub = this.dataService.downloadDiagramImageInk(this.id)
		.subscribe({
			next: (data) => {
				console.log(data)
			},
			error: (e) => {
				console.log(e)
			},
		})
	}

	generateDiagram() {
		this.generateDiagramLoading = true
		this.generateDiagramSuccess = false
		this.generateDiagramError = false

		this.generateDiagramSub = this.dataService.generateDiagramAI(this.courseID, this.parentType, this.parentID)
		.subscribe({
			next: (data) => {
				console.log(data)
				this.generateDiagramLoading = false
				this.generateDiagramSuccess = true
				this.generateDiagramError = false

				this.title = data.title
				this.code = data.code
				this.updateDiagramURL()
				this.renderDiagram()
				// this.updateDiagram()
			},
			error: (e) => {
				console.log(e)
				this.generateDiagramLoading = false
				this.generateDiagramSuccess = false
				this.generateDiagramError = true
			},
		})
	}

}
